import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import Modal from "../components/Modal";
import {toast, ToastContainer, ToastPosition, TypeOptions} from "react-toastify";
import {routes} from "../ZrRouter";
import {PluginData} from "../types/PluginData";
import {useFetchSingle} from "../hooks/useFetchSingle";
import {ModalData} from "../types/ModalData";
import i18n from "../i18n";

type GlobalContextType = {
    useRoute: {
        route: keyof typeof routes, 
        changeRoute: React.Dispatch<React.SetStateAction<keyof typeof routes>>,
        routeParams: any,
        changeRouteWithParams: (newRoute: keyof typeof routes, params?: any) => void
    };
    history: string[];

    useModal: {
        modal: ModalData;
        showModal: (data: {heading?: React.ReactNode, content: React.ReactNode, fullscreen: boolean}) => void;
        closeModal: () => void;
    }
    showModal: (data: {heading?: React.ReactNode, content: React.ReactNode, fullscreen: boolean, size?: string}) => void;
    hideModal: () => void;
    showToast: (
        position: ToastPosition,
        type: 'info' | 'success' | 'warning' | 'error' | 'default',
        message: string
    ) => void;

    useFullscreen: {fullscreen: boolean, setFullscreen: React.Dispatch<React.SetStateAction<boolean>>}
    plugin: PluginData|null;
}

const GlobalContext = createContext<GlobalContextType|undefined>(undefined)

type GlobalContextProps = {
    children: React.ReactNode|React.ReactNode[],
}

export const GlobalContextProvider = ({children}: GlobalContextProps): JSX.Element => {
    const [fullscreen, setFullscreen] = useState<boolean>(false)

    const pluginSlug = useMemo(() => {
        const wrapper = document.getElementById('zr-wrapper')
        return  (wrapper?.dataset.plugin ?? 'zr_plugin')
    }, [])

    const [route, setRoute] = useState<keyof typeof routes>('overview')

    const [routeParams, setRouteParams] = useState<any>(null)
    const [history, setHistory] = useState<string[]>([])

    const changeRouteWithParams = useCallback((newRoute: keyof typeof routes, params?: any) => {
        setRoute(newRoute);
        setRouteParams(params || null);
    }, []);

    const [modal, setModal] = useState<ModalData>({
        content: '',
        visible: false,
        fullscreen: false
    })

    const showToast = (position: ToastPosition, type: TypeOptions, message: string) => {
        toast.success(message, {
            position: position,
            type : type,
        });
    }

    const showModal = useCallback((data: {
        heading?: React.ReactNode,
        content: React.ReactNode,
        fullscreen: boolean, width?: string
    }) => {
        setModal((prevModal) => ({
            ...prevModal,
            visible: true,
            heading: data.heading,
            content: data.content,
            fullscreen: data.fullscreen,
            width: data.width
        }))
    }, [])

    const closeModal = useCallback(() => {
        setModal((prevModal) => ({
            ...prevModal,
            visible: false,
        }))
    }, [])

    const {
        data: plugin,
        fetch: fetchPlugin
    } = useFetchSingle<PluginData>('plugin')

    useEffect(() => {
        localStorage.setItem(pluginSlug, route)
    }, [route, pluginSlug])

    useEffect(() => {
        setHistory((prevHistory) => [...prevHistory, route])
    }, [route])

    useEffect(() => {
        if (fetchPlugin) fetchPlugin(window.location.href.includes('admin') ? 'admin' : 'frontend')
    }, [fetchPlugin])

    useEffect(() => {
        if (!plugin) return
        if (plugin.language && plugin.language.i18n) {
            i18n.changeLanguage(plugin.language.i18n).then(() => {
                localStorage.setItem('language', plugin.language.i18n)
            })
        }
    }, [plugin, i18n])


    return (
        <GlobalContext.Provider value={{
            useRoute: {route, changeRoute: setRoute, routeParams, changeRouteWithParams},
            useFullscreen: {fullscreen, setFullscreen},
            useModal: {modal, showModal, closeModal},
            history,
            showModal,
            hideModal: closeModal,
            showToast,
            plugin,
        }}>
            <Modal/>
            {children}
            <ToastContainer/>
        </GlobalContext.Provider>
    );
}

export const useGlobalContext = () => {
    const data = useContext(GlobalContext)

    if (data === undefined) {
        throw new Error('useGlobalContext must used within GlobalContextProvider')
    }

    return data
}

